import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "launchModal", "resultsModal",
    "launchIcon", "launchName", "launchDescription", "launchDuration",
    "workerCount", "workerRange", "baseCost", "boostOptions",
    "oddsInfiltration", "oddsCombat", "oddsExtraction", "oddsOverall",
    "bonusProgress", "launchButton",
    "resultsIcon", "resultsName", "resultsBadge",
    "phaseLog", "lootSection", "lootLabel", "lootList",
    "bonusSection", "bonusText", "bonusNarrative",
    "workerStatus", "collectButton"
  ]

  static values = {
    targetKey: String,
    config: Object,
    availableWorkers: Number,
    launchUrl: String,
    raidId: Number,
    phaseLog: Array,
    rewards: Object,
    collectUrl: String
  }

  connect() {
    this.workers = 0
    this.selectedBoosts = []
  }

  openLaunch(event) {
    const el = event.currentTarget
    this.configValue = JSON.parse(el.dataset.raidModalConfigValue)
    this.targetKeyValue = el.dataset.raidModalTargetKeyValue
    this.availableWorkersValue = parseInt(el.dataset.raidModalAvailableWorkersValue)
    this.launchUrlValue = el.dataset.raidModalLaunchUrlValue

    const config = this.configValue
    this.workers = config.min_workers
    this.selectedBoosts = []

    this.launchIconTarget.textContent = config.icon
    this.launchNameTarget.textContent = config.name
    this.launchDescriptionTarget.textContent = `"${config.description}"`
    const duration = config.duration_seconds >= 3600
      ? `${config.duration_seconds / 3600} hours`
      : `${config.duration_seconds / 60} minutes`
    this.launchDurationTarget.textContent = `⏱ Duration: ${duration}`

    this.renderBaseCost(config)
    this.renderBoostOptions(config)
    this.renderBonusProgress(config)
    this.updateWorkerDisplay(config)
    this.updateOdds(config)

    this.launchModalTarget.classList.remove("hidden")
    this.launchModalTarget.classList.add("flex")
  }

  closeLaunch() {
    this.launchModalTarget.classList.add("hidden")
    this.launchModalTarget.classList.remove("flex")
  }

  incrementWorkers() {
    const config = this.configValue
    const max = Math.min(config.min_workers + 3, this.availableWorkersValue)
    if (this.workers < max) {
      this.workers++
      this.updateWorkerDisplay(config)
      this.updateOdds(config)
    }
  }

  decrementWorkers() {
    const config = this.configValue
    if (this.workers > config.min_workers) {
      this.workers--
      this.updateWorkerDisplay(config)
      this.updateOdds(config)
    }
  }

  toggleBoost(event) {
    const phase = event.currentTarget.dataset.boostPhase
    const idx = this.selectedBoosts.indexOf(phase)
    if (idx >= 0) {
      this.selectedBoosts.splice(idx, 1)
    } else {
      this.selectedBoosts.push(phase)
    }
    this.updateOdds(this.configValue)
  }

  updateWorkerDisplay(config) {
    const max = Math.min(config.min_workers + 3, this.availableWorkersValue)
    this.workerCountTarget.textContent = this.workers
    this.workerRangeTarget.textContent = `(min ${config.min_workers}, max ${max})`
  }

  renderBaseCost(config) {
    const cost = config.cost || {}
    const parts = Object.entries(cost).map(([rt, amt]) => `${amt} ${rt}`).join(" · ")
    this.baseCostTarget.textContent = parts
  }

  renderBoostOptions(config) {
    const boosts = config.boosts || {}
    const phaseNames = { infiltration: "Infiltration", combat: "Combat", extraction: "Extraction" }
    let html = ""
    for (const [phase, boost] of Object.entries(boosts)) {
      html += `<label class="flex items-center gap-2 text-xs cursor-pointer p-1 bg-[#1a1a1a] rounded">
        <input type="checkbox" data-boost-phase="${phase}" data-action="raid-modal#toggleBoost"
               style="accent-color:#f59e0b;">
        +${boost.bonus}% ${phaseNames[phase]} — ${boost.cost} ${boost.resource}
      </label>`
    }
    this.boostOptionsTarget.innerHTML = html
  }

  renderBonusProgress(config) {
    if (!config.bonus) {
      this.bonusProgressTarget.textContent = ""
      return
    }
    const type = config.bonus.type.replace(/_/g, " ")
    this.bonusProgressTarget.textContent = `Bonus: ${type} (${config.bonus.chance}% chance on full success)`
  }

  updateOdds(config) {
    const extraWorkers = Math.max(this.workers - config.min_workers, 0)
    const workerBonus = Math.min(extraWorkers * config.worker_bonus_per_extra, config.max_worker_bonus)

    const phases = ["infiltration", "combat", "extraction"]
    const odds = {}
    phases.forEach(phase => {
      const base = config.phases[phase].base_success
      const boostBonus = this.selectedBoosts.includes(phase) ? config.boosts[phase].bonus : 0
      odds[phase] = Math.min(base + workerBonus + boostBonus, 100)
    })

    const colorFor = (pct) => pct >= 90 ? "color:#22c55e" : pct >= 70 ? "color:#f59e0b" : "color:#ef4444"

    this.oddsInfiltrationTarget.innerHTML = `<span style="${colorFor(odds.infiltration)}">${odds.infiltration}%</span>`
    this.oddsCombatTarget.innerHTML = `<span style="${colorFor(odds.combat)}">${odds.combat}%</span>`
    this.oddsExtractionTarget.innerHTML = `<span style="${colorFor(odds.extraction)}">${odds.extraction}%</span>`

    const overall = Math.round((odds.infiltration / 100) * (odds.combat / 100) * (odds.extraction / 100) * 100)
    this.oddsOverallTarget.innerHTML = `<span style="${colorFor(overall)}">${overall}%</span>`
  }

  async launch() {
    this.launchButtonTarget.disabled = true
    this.launchButtonTarget.textContent = "Launching..."

    const body = new FormData()
    body.append("raid_target", this.targetKeyValue)
    body.append("workers", this.workers)
    this.selectedBoosts.forEach(b => body.append("boosts[]", b))

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await fetch(this.launchUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": token, "Accept": "text/vnd.turbo-stream.html" },
      body: body
    })

    if (response.ok) {
      this.closeLaunch()
    } else {
      const html = await response.text()
      Turbo.renderStreamMessage(html)
      this.launchButtonTarget.disabled = false
      this.launchButtonTarget.textContent = "⚔️ Launch Raid"
    }
  }

  openResults(event) {
    const el = event.currentTarget
    this.configValue = JSON.parse(el.dataset.raidModalConfigValue)
    this.rewardsValue = JSON.parse(el.dataset.raidModalRewardsValue)
    this.phaseLogValue = JSON.parse(el.dataset.raidModalPhaseLogValue)
    this.collectUrlValue = el.dataset.raidModalCollectUrlValue

    const config = this.configValue
    const rewards = this.rewardsValue
    const phaseLog = this.phaseLogValue

    this.resultsIconTarget.textContent = config.icon
    this.resultsNameTarget.textContent = `${config.name} — Raid Complete`

    // Badge
    const outcome = rewards.outcome
    if (outcome === "full_success") {
      this.resultsBadgeTarget.innerHTML = '<span style="color:#22c55e">⚔️ FULL SUCCESS</span>'
    } else if (outcome === "failure") {
      this.resultsBadgeTarget.innerHTML = '<span style="color:#ef4444">✗ RAID FAILED</span>'
    } else {
      this.resultsBadgeTarget.innerHTML = '<span style="color:#f59e0b">⚠️ PARTIAL SUCCESS</span>'
    }

    // Phase log
    let logHtml = '<div style="border-left:2px solid #374151; padding-left:10px;">'
    phaseLog.forEach(p => {
      const color = p.success ? "#22c55e" : "#ef4444"
      const icon = p.success ? "✓" : "✗"
      const phaseName = p.phase.charAt(0).toUpperCase() + p.phase.slice(1)
      logHtml += `<div class="mb-2">
        <div class="text-xs uppercase tracking-wider" style="color:${color}">Phase — ${phaseName} ${icon}</div>
        <div class="text-xs text-[#d1d5db] italic">"${p.narrative}"</div>
      </div>`
    })
    logHtml += '</div>'
    this.phaseLogTarget.innerHTML = logHtml

    // Loot
    const resources = rewards.resources || {}
    const hasLoot = Object.values(resources).some(v => v > 0)
    if (hasLoot) {
      this.lootSectionTarget.classList.remove("hidden")
      this.lootSectionTarget.className = outcome === "full_success"
        ? "bg-[#0a1a0a] border border-green-700/50 rounded-md p-2 mb-2"
        : "bg-[#1a1500] border border-amber-500/30 rounded-md p-2 mb-2"
      this.lootLabelTarget.textContent = outcome === "full_success" ? "Salvage Recovered" : "Partial Salvage"
      this.lootLabelTarget.style.color = outcome === "full_success" ? "#22c55e" : "#f59e0b"
      const lootParts = Object.entries(resources).filter(([, v]) => v > 0).map(([rt, v]) => `${v} ${rt}`).join(" · ")
      this.lootListTarget.textContent = lootParts
    } else {
      this.lootSectionTarget.classList.add("hidden")
    }

    // Bonus
    if (rewards.bonus) {
      this.bonusSectionTarget.classList.remove("hidden")
      const bonusType = rewards.bonus.type.replace(/_/g, " ")
      this.bonusTextTarget.textContent = `+${rewards.bonus.value} ${bonusType}`
      this.bonusNarrativeTarget.textContent = ""
    } else {
      this.bonusSectionTarget.classList.add("hidden")
    }

    // Worker status
    const lost = rewards.workers_lost || 0
    if (lost > 0) {
      this.workerStatusTarget.innerHTML = `<span style="color:#ef4444">💀 ${lost} worker${lost > 1 ? 's' : ''} lost in action</span>`
    } else {
      this.workerStatusTarget.textContent = "👷 All workers returned safely"
    }

    // Collect button
    if (outcome === "failure" && !hasLoot) {
      this.collectButtonTarget.textContent = "Dismiss"
      this.collectButtonTarget.className =
        "w-full bg-[#374151] hover:bg-[#4b5563] text-[#9ca3af] text-sm py-2 rounded-md font-bold"
    } else {
      this.collectButtonTarget.textContent = "Collect Rewards"
      this.collectButtonTarget.className =
        "w-full bg-amber-700 hover:bg-amber-600 text-white text-sm py-2 rounded-md font-bold"
    }

    this.resultsModalTarget.classList.remove("hidden")
    this.resultsModalTarget.classList.add("flex")
  }

  async collectOrDismiss() {
    this.collectButtonTarget.disabled = true
    this.collectButtonTarget.textContent = "Collecting..."

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await fetch(this.collectUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": token, "Accept": "text/vnd.turbo-stream.html" }
    })

    if (response.ok) {
      this.resultsModalTarget.classList.add("hidden")
      this.resultsModalTarget.classList.remove("flex")
    } else {
      const html = await response.text()
      Turbo.renderStreamMessage(html)
      this.collectButtonTarget.disabled = false
      this.collectButtonTarget.textContent = "Collect Rewards"
    }
  }
}
